import { LodgingErrorEnum } from "@b2bportal/lodging-api";
import { PurchaseErrorEnum } from "@b2bportal/purchase-api";

import {
  LodgingErrorAction,
  type LodgingErrorConfig,
  type LodgingErrorConfigMap,
  type LodgingPurchaseErrorConfigMap,
} from "./types";

/**
 * Translation key prefix for checkout errors
 */
const PREFIX = "checkoutError";

/**
 * Helper to create a button config
 */
const button = (textKey: string, action: LodgingErrorAction) => ({
  textKey: `${PREFIX}.${textKey}`,
  action,
});

/**
 * Common button configurations
 */
const BUTTONS = {
  searchAgain: button("searchAgain", LodgingErrorAction.NavigateToHotelSearch),
  findNewHotel: button(
    "findNewHotel",
    LodgingErrorAction.NavigateToHotelSearch
  ),
  selectAnotherRoom: button(
    "selectAnotherRoom",
    LodgingErrorAction.SelectAnotherRoom
  ),
  updateTravelers: button(
    "updateTraveler",
    LodgingErrorAction.OpenTravelerPicker
  ),
  updateTravelersGoBack: button(
    "updateTraveler",
    LodgingErrorAction.GoBackToTravelerInfo
  ),
  tryAgain: button("tryAgain", LodgingErrorAction.RetryQuote),
};

/**
 * Default error configuration for fallback
 */
const DEFAULT_CART_QUOTE_CONFIG: LodgingErrorConfig = {
  titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
  subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
  primaryButton: BUTTONS.tryAgain,
  secondaryButton: BUTTONS.findNewHotel,
};

const DEFAULT_CART_FULFILL_CONFIG: LodgingErrorConfig = {
  titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
  subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
  primaryButton: BUTTONS.tryAgain,
  secondaryButton: BUTTONS.findNewHotel,
};

/**
 * Lodging Error Configuration Map
 *
 * This configuration maps each LodgingErrorEnum code to its error modal configuration
 * for both CART_QUOTE and CART_FULFILL contexts.
 *
 * Based on the error specification document.
 */
export const LODGING_ERROR_CONFIG: LodgingErrorConfigMap = {
  CART_QUOTE: {
    default: DEFAULT_CART_QUOTE_CONFIG,

    // ============================================================
    // Age requirement errors
    // ============================================================

    [LodgingErrorEnum.CheckInMinimumAgeNotMet]: {
      titleKey: `${PREFIX}.hotelCheckInMinimumAgeNotMetTitle`,
      subtitleKey: `${PREFIX}.hotelCheckInMinimumAgeNotMetSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
    },

    [LodgingErrorEnum.MinimumCheckInAgeNotMet]: {
      titleKey: `${PREFIX}.hotelCheckInMinimumAgeNotMetTitle`,
      subtitleKey: `${PREFIX}.hotelCheckInMinimumAgeNotMetSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
    },

    // ============================================================
    // Availability errors
    // ============================================================

    [LodgingErrorEnum.RateNotAvailable]: {
      titleKey: `${PREFIX}.hotelRateNotAvailableTitle`,
      subtitleKey: `${PREFIX}.hotelRateNotAvailableSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [LodgingErrorEnum.LackOfInventory]: {
      titleKey: `${PREFIX}.hotelLackOfInventoryTitle`,
      subtitleKey: `${PREFIX}.hotelLackOfInventorySubtitle`,
      primaryButton: BUTTONS.selectAnotherRoom,
    },

    [LodgingErrorEnum.Unavailable]: {
      titleKey: `${PREFIX}.hotelLackOfInventoryTitle`,
      subtitleKey: `${PREFIX}.hotelLackOfInventorySubtitle`,
      primaryButton: BUTTONS.selectAnotherRoom,
    },

    // ============================================================
    // Quote errors
    // ============================================================

    [LodgingErrorEnum.InvalidQuote]: {
      titleKey: `${PREFIX}.hotelInvalidQuoteTitle`,
      subtitleKey: `${PREFIX}.hotelInvalidQuoteSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [LodgingErrorEnum.OfferNotFound]: {
      titleKey: `${PREFIX}.hotelLackOfInventoryTitle`,
      subtitleKey: `${PREFIX}.hotelLackOfInventorySubtitle`,
      primaryButton: BUTTONS.selectAnotherRoom,
    },

    [LodgingErrorEnum.QuoteExpired]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    // ============================================================
    // Payment errors
    // ============================================================

    [LodgingErrorEnum.PaymentFailed]: {
      titleKey: `${PREFIX}.hotelPaymentFailedTitle`,
      subtitleKey: `${PREFIX}.hotelPaymentFailedSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    // ============================================================
    // Generic/Service errors
    // ============================================================

    [LodgingErrorEnum.ServiceError]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    [LodgingErrorEnum.Unspecified]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    [LodgingErrorEnum.ClientNotDefined]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    [LodgingErrorEnum.ProductNotFound]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    [LodgingErrorEnum.ProviderNotDefined]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    [LodgingErrorEnum.LodgingErrorCode]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    [LodgingErrorEnum.FrequentGuestNumberRequired]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },
  },

  CART_FULFILL: {
    default: DEFAULT_CART_FULFILL_CONFIG,

    // ============================================================
    // Age requirement errors
    // ============================================================

    [LodgingErrorEnum.CheckInMinimumAgeNotMet]: {
      titleKey: `${PREFIX}.hotelCheckInMinimumAgeNotMetTitle`,
      subtitleKey: `${PREFIX}.hotelCheckInMinimumAgeNotMetSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
    },

    [LodgingErrorEnum.MinimumCheckInAgeNotMet]: {
      titleKey: `${PREFIX}.hotelCheckInMinimumAgeNotMetTitle`,
      subtitleKey: `${PREFIX}.hotelCheckInMinimumAgeNotMetSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
    },

    // ============================================================
    // Availability errors
    // ============================================================

    [LodgingErrorEnum.RateNotAvailable]: {
      titleKey: `${PREFIX}.hotelRateNotAvailableTitle`,
      subtitleKey: `${PREFIX}.hotelRateNotAvailableSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [LodgingErrorEnum.LackOfInventory]: {
      titleKey: `${PREFIX}.hotelLackOfInventoryTitle`,
      subtitleKey: `${PREFIX}.hotelLackOfInventorySubtitle`,
      primaryButton: BUTTONS.selectAnotherRoom,
    },

    [LodgingErrorEnum.Unavailable]: {
      titleKey: `${PREFIX}.hotelLackOfInventoryTitle`,
      subtitleKey: `${PREFIX}.hotelLackOfInventorySubtitle`,
      primaryButton: BUTTONS.selectAnotherRoom,
    },

    // ============================================================
    // Quote errors
    // ============================================================

    [LodgingErrorEnum.InvalidQuote]: {
      titleKey: `${PREFIX}.hotelInvalidQuoteTitle`,
      subtitleKey: `${PREFIX}.hotelInvalidQuoteSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [LodgingErrorEnum.OfferNotFound]: {
      titleKey: `${PREFIX}.hotelLackOfInventoryTitle`,
      subtitleKey: `${PREFIX}.hotelLackOfInventorySubtitle`,
      primaryButton: BUTTONS.selectAnotherRoom,
    },

    [LodgingErrorEnum.QuoteExpired]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    // ============================================================
    // Payment errors
    // ============================================================

    [LodgingErrorEnum.PaymentFailed]: {
      titleKey: `${PREFIX}.hotelPaymentFailedTitle`,
      subtitleKey: `${PREFIX}.hotelPaymentFailedSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    // ============================================================
    // Generic/Service errors
    // ============================================================

    [LodgingErrorEnum.ServiceError]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    [LodgingErrorEnum.Unspecified]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    [LodgingErrorEnum.ClientNotDefined]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    [LodgingErrorEnum.ProductNotFound]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    [LodgingErrorEnum.ProviderNotDefined]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    [LodgingErrorEnum.LodgingErrorCode]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    [LodgingErrorEnum.FrequentGuestNumberRequired]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },
  },
};

/**
 * Purchase-level error configuration for lodging
 *
 * These override the default handling for specific PurchaseErrorEnum categories
 */
export const LODGING_PURCHASE_ERROR_CONFIG: LodgingPurchaseErrorConfigMap = {
  CART_QUOTE: {
    [PurchaseErrorEnum.NoAvailability]: {
      titleKey: `${PREFIX}.noAvailabilityTitle`,
      subtitleKey: `${PREFIX}.noAvailabilitySubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [PurchaseErrorEnum.ExpiredFulfillToken]: {
      titleKey: `${PREFIX}.noAvailabilityTitle`,
      subtitleKey: `${PREFIX}.noAvailabilitySubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [PurchaseErrorEnum.FraudAutoReject]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.findNewHotel,
    },

    [PurchaseErrorEnum.InvalidSession]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [PurchaseErrorEnum.AuthError]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    [PurchaseErrorEnum.ErrorCode]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    [PurchaseErrorEnum.ExpiredQuoteToken]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    [PurchaseErrorEnum.InActivity]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    [PurchaseErrorEnum.NonDistinctPayments]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    [PurchaseErrorEnum.NoPaymentsProvided]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },
  },

  CART_FULFILL: {
    [PurchaseErrorEnum.NoAvailability]: {
      titleKey: `${PREFIX}.noAvailabilityTitle`,
      subtitleKey: `${PREFIX}.noAvailabilitySubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [PurchaseErrorEnum.ExpiredFulfillToken]: {
      titleKey: `${PREFIX}.noAvailabilityTitle`,
      subtitleKey: `${PREFIX}.noAvailabilitySubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [PurchaseErrorEnum.FraudAutoReject]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.findNewHotel,
    },

    [PurchaseErrorEnum.InvalidSession]: {
      titleKey: `${PREFIX}.searchAgain`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [PurchaseErrorEnum.AuthError]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    [PurchaseErrorEnum.ErrorCode]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    [PurchaseErrorEnum.ExpiredQuoteToken]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    [PurchaseErrorEnum.InActivity]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    [PurchaseErrorEnum.NonDistinctPayments]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },

    [PurchaseErrorEnum.NoPaymentsProvided]: {
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.tryAgain,
      secondaryButton: BUTTONS.findNewHotel,
    },
  },
};

/**
 * Helper to get lodging error config with fallback to default
 */
export const getLodgingErrorConfig = (
  context: "CART_QUOTE" | "CART_FULFILL",
  errorCode: LodgingErrorEnum
): LodgingErrorConfig => {
  return (
    LODGING_ERROR_CONFIG[context][errorCode] ??
    LODGING_ERROR_CONFIG[context].default
  );
};

/**
 * Helper to get lodging purchase error config with fallback
 */
export const getLodgingPurchaseErrorConfig = (
  context: "CART_QUOTE" | "CART_FULFILL",
  category: PurchaseErrorEnum
): LodgingErrorConfig | undefined => {
  return LODGING_PURCHASE_ERROR_CONFIG[context][
    category as keyof (typeof LODGING_PURCHASE_ERROR_CONFIG)[typeof context]
  ];
};
